/**
 * System / Operations settings — the operational toggles + thresholds that were
 * previously SQL-only (outage alerting on/off + milestones, edge-software
 * upgrade targets, auto-ticketing, runner kill switches, a few classifier
 * thresholds). Same platform-only /api/admin/settings endpoint as the Mail tab,
 * filtered to category='system'. The allow-list (settings_admin.py EDITABLE)
 * is the authority on what appears here; secrets and the whole-builder kill
 * switch are deliberately excluded server-side.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { errMsg } from "../../lib/errMsg";
import {
  type AdminSetting,
  listSettings,
  updateSettings,
} from "../../services/settingsAdminApi";

const SECTION_LABEL: Record<string, string> = {
  MttrSettings: "Operations",
  MttrSoftwareTargets: "Edge software upgrade targets",
};

export const SystemSettingsTab: React.FC = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState<AdminSetting[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const rows = (await listSettings(token)).filter((s) => s.category === "system");
      setSettings(rows);
      const d: Record<string, string> = {};
      rows.forEach((s) => {
        d[s.id] = s.value ?? "";
      });
      setDraft(d);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const grouped = useMemo(() => {
    const out: Record<string, AdminSetting[]> = {};
    settings.forEach((s) => {
      (out[s.section] ||= []).push(s);
    });
    return out;
  }, [settings]);

  const changes = useMemo(() => {
    const out: Record<string, string> = {};
    settings.forEach((s) => {
      const now = draft[s.id] ?? "";
      const orig = s.value ?? "";
      if (now !== orig) out[s.id] = now;
    });
    return out;
  }, [settings, draft]);
  const dirtyCount = Object.keys(changes).length;

  const save = async () => {
    if (!token || dirtyCount === 0) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await updateSettings(token, changes);
      setNotice(`Saved ${res.updated.length} change${res.updated.length === 1 ? "" : "s"}.`);
      await load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const field = (s: AdminSetting) => {
    const value = draft[s.id] ?? "";
    const set = (v: string) => setDraft((p) => ({ ...p, [s.id]: v }));
    if (s.value_type === "bool") {
      return (
        <select
          className="border border-secondary-300 rounded px-2 py-1 text-sm w-full max-w-xs"
          value={value === "" ? "false" : value}
          onChange={(e) => set(e.target.value)}
        >
          <option value="true">Enabled</option>
          <option value="false">Disabled</option>
        </select>
      );
    }
    return (
      <input
        type="text"
        className="border border-secondary-300 rounded px-2 py-1 text-sm w-full max-w-md font-mono"
        value={value}
        inputMode={s.value_type === "int" ? "numeric" : undefined}
        onChange={(e) => set(e.target.value)}
      />
    );
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-secondary-600">
        Operational toggles + thresholds. Changes take effect on the next runner
        cycle (usually within a minute) — no deploy needed.
      </p>
      {error && (
        <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{error}</div>
      )}
      {notice && (
        <div className="text-sm text-success-700 bg-success-50 px-3 py-2 rounded">{notice}</div>
      )}
      {loading && <div className="text-sm text-secondary-500">Loading…</div>}

      {Object.entries(grouped).map(([section, rows]) => (
        <section key={section}>
          <h2 className="text-sm font-semibold mb-2">{SECTION_LABEL[section] ?? section}</h2>
          <div className="space-y-3">
            {rows.map((s) => (
              <div key={s.id} className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3">
                <label className="text-sm text-secondary-700 md:w-72 shrink-0">{s.label}</label>
                <div className="flex-1">
                  {field(s)}
                  {s.help && (
                    <div className="text-[11px] text-secondary-500 mt-0.5">{s.help}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-secondary-200">
        <Button onClick={() => void save()} disabled={saving || dirtyCount === 0}>
          {saving
            ? "Saving…"
            : dirtyCount
              ? `Save ${dirtyCount} change${dirtyCount === 1 ? "" : "s"}`
              : "Save"}
        </Button>
        {dirtyCount > 0 && <span className="text-xs text-amber-700">Unsaved changes</span>}
      </div>
    </div>
  );
};

export default SystemSettingsTab;
